import React, { useEffect, useState, useMemo } from "react";
import AuthenticatedLayout from "../layouts/AuthenticatedLayout";
import InputField from "../components/InputField";
import Notification from "../components/Notification";
import { useAuth } from "../hooks/AuthProvider";
import InvoicesTable from "../components/InvoicesTable";
import { getTags } from "../services/tagService";

const SearchPage = () => {
	const [invoices, setInvoices] = useState(null);
	const [descFilter, setDescFilter] = useState("");
	const [startDateFilter, setStartDateFilter] = useState("");
	const [endDateFilter, setEndDateFilter] = useState("");

	const [tags, setTags] = useState([]);
	const [excludedTagIds, setExcludedTagIds] = useState([]);

	const [success, setSuccess] = useState(null);
	const [error, setError] = useState(null);
	const [searchParams, setSearchParams] = useState({
		descr: "",
		startDate: "",
		endDate: "",
		excludedTagIds: [],
	});

	const auth = useAuth();

	useEffect(() => {
		// carica TUTTI i tag (visibili + hidden)
		getTags(auth, null)
			.then((data) => setTags(Array.isArray(data) ? data : []))
			.catch((e) => setError(e?.message || "Errore nel caricamento tag"));
	}, [auth]);

	const handleSearch = (e) => {
		if (e) e.preventDefault();
		setSearchParams({
			descr: descFilter,
			startDate: startDateFilter,
			endDate: endDateFilter,
			excludedTagIds: excludedTagIds,
		});
	};

	const handleClearSearch = () => {
		setDescFilter("");
		setStartDateFilter("");
		setEndDateFilter("");
		setExcludedTagIds([]);
		setSearchParams({ descr: "", startDate: "", endDate: "", excludedTagIds: [] });
		setInvoices(null);
	};

	const visibleTags = useMemo(() => tags.filter((t) => !t.hidden), [tags]);
	const hiddenTags = useMemo(() => tags.filter((t) => !!t.hidden), [tags]);

	const toggleExcluded = (tagId) => {
		setExcludedTagIds((prev) =>
			prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
		);
	};

	const isActive = (id) => excludedTagIds.includes(id);

	const hasActiveFilters =
		searchParams.descr !== "" ||
		searchParams.startDate !== "" ||
		searchParams.endDate !== "" ||
		(searchParams.excludedTagIds && searchParams.excludedTagIds.length > 0);

	return (
		<AuthenticatedLayout title="Cerca Fatture">
			{error && <Notification notification={{ type: 0, message: error }} />}
			{success && <Notification notification={{ type: 3, message: success }} />}

			<form className="mb-5" onSubmit={handleSearch}>
				<div className="columns">
					<div className="column">
						<InputField
							className="column"
							type="date"
							label="Data Inizio"
							value={startDateFilter}
							onChange={(e) => setStartDateFilter(e.target.value)}
						/>
					</div>
					<div className="column">
						<InputField
							className="column "
							type="date"
							label="Data Fine"
							value={endDateFilter}
							onChange={(e) => setEndDateFilter(e.target.value)}
						/>
					</div>
				</div>
				<div className="columns">
					<div className="column">
						<InputField
							className="column"
							type="text"
							label="Descrizione"
							value={descFilter}
							onChange={(e) => setDescFilter(e.target.value)}
							placeholder="Filtra Per Descrizione"
						/>
					</div>
				</div>

				{/* Escludi Tag */}
				<div className="mb-4">
					<label className="label">Escludi Fatture con Tag</label>

					<div className="box">
						<p className="has-text-weight-semibold mb-2">Tag visibili</p>
						<div className="tags are-medium is-flex is-flex-wrap-wrap">
							{visibleTags.map((t) => (
								<label
									key={t.id}
									className={`tag m-1 is-clickable ${
										isActive(t.id) ? "is-danger" : "is-light"
									}`}
									onClick={() => toggleExcluded(t.id)}
									title="Clicca per includere/escludere"
								>
									<span
										style={{
											display: "inline-block",
											width: 10,
											height: 10,
											background: t.hex_color,
											borderRadius: 2,
											marginRight: 8,
										}}
									/>
									{t.tag}
								</label>
							))}
							{visibleTags.length === 0 && (
								<span className="has-text-grey">Nessun tag visibile.</span>
							)}
						</div>

						<p className="has-text-weight-semibold mt-4 mb-2">Tag nascosti</p>
						<div className="tags are-medium is-flex is-flex-wrap-wrap">
							{hiddenTags.map((t) => (
								<label
									key={t.id}
									className={`tag m-1 is-clickable ${
										isActive(t.id) ? "is-danger" : "is-light"
									}`}
									onClick={() => toggleExcluded(t.id)}
									title="Clicca per includere/escludere"
								>
									<span
										style={{
											display: "inline-block",
											width: 10,
											height: 10,
											background: t.hex_color,
											borderRadius: 2,
											marginRight: 8,
										}}
									/>
									{t.tag}{" "}
								</label>
							))}
							{hiddenTags.length === 0 && (
								<span className="has-text-grey">Nessun tag nascosto.</span>
							)}
						</div>
					</div>
				</div>

				<div className="has-text-centered">
					<button className="button is-primary is-rounded mr-2" type="submit">
						Filtra
					</button>
					<button
						className="button is-secondary is-rounded ml-2"
						onClick={handleClearSearch}
						type="button"
					>
						Reset Filtri
					</button>
				</div>
			</form>

			{hasActiveFilters && (
				<InvoicesTable
					mode="search"
					invoices={invoices}
					searchParams={searchParams}
					setError={setError}
					setInvoices={setInvoices}
					auth={auth}
					setSuccess={setSuccess}
				/>
			)}
		</AuthenticatedLayout>
	);
};

export default SearchPage;
